import {useTranslation} from 'react-i18next';

import {Box, Typography} from '@mui/material';

import {ProfileComparisonScorePill} from './ProfileComparisonScorePill';
import {profileComparisonCardBaseSx} from './profileComparisonSectionStyles';
import {profileComparisonSelection} from '../../../../../layouts/theme/designSystem';
import {getSeniorityLevelLabel} from '../../../../../utils/getSeniorityLevelLabel';
import type {CompanyWorkforceProfile} from '../../types/WorkforceProfileTypes';
import {getWorkforceProfileDisplayId} from '../../utils/WorkforceProfiles/getWorkforceProfileDisplayId';

export type ProfileComparisonCardSelection = 'a' | 'b' | null;

export interface Props {
  profile: CompanyWorkforceProfile;
  selection: ProfileComparisonCardSelection;
  onClick: () => void;
}

export const ProfileComparisonProfileCard = ({profile, selection, onClick}: Props) => {
  const {t} = useTranslation();
  const {t: tCard} = useTranslation('translation', {
    keyPrefix: 'workforceDepartmentHealthPage.profileComparison.card',
  });

  const displayId = getWorkforceProfileDisplayId(profile);
  const targetRole = profile.companyDepartmentTargetProfile?.title ?? '—';
  const seniorityLabel = getSeniorityLevelLabel(profile.seniority, t);
  const score = profile.alignment?.overall;

  const selectionColors =
    selection === 'a' ? profileComparisonSelection.slotA : profileComparisonSelection.slotB;

  const cardSx = selection
    ? {
      ...profileComparisonCardBaseSx,
      borderColor: selectionColors.borderColor,
      bgcolor: selectionColors.bgcolor,
      '&:hover': {
        borderColor: selectionColors.borderColor,
        bgcolor: selectionColors.bgcolor,
      },
    }
    : profileComparisonCardBaseSx;

  return (
    <Box onClick={onClick} sx={cardSx} data-testid={`profile-comparison-card-${profile.resourceId}`}>
      <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.375}}>
        <Typography sx={{fontSize: '12px', fontWeight: 600, flex: 1}}>{displayId}</Typography>
        {score != null && <ProfileComparisonScorePill score={score} />}
      </Box>
      <Typography sx={{fontSize: '11px', color: 'text.secondary'}}>{targetRole}</Typography>
      <Typography sx={{fontSize: '11px', color: 'text.secondary'}}>{seniorityLabel}</Typography>
      {selection && (
        <Typography
          sx={{
            mt: 0.625,
            fontSize: '10px',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            color: selectionColors.color,
          }}
        >
          {selection === 'a' ? tCard('profileA') : tCard('profileB')}
        </Typography>
      )}
    </Box>
  );
};
